import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  getProjects, getCustomers, createCodeDocs, getJobStatus, getDocumentationById,
  exportDocumentationWord, exportDocumentationPdf, downloadExport,
} from '../services/api';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ArrowLeft, Loader2, GitBranch, Link, KeyRound, ChevronDown, ChevronUp,
  FileSearch, BookMarked, Download, FileDown, FolderOpen, UserRound, PencilRuler,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import JobProgressTracker from '../components/upload/JobProgressTracker';
import FileUploadTab from '../components/upload/FileUploadTab';
import { zipLooseFiles, uploadingStatus } from '../utils/slimZip';

/**
 * Code Documentation page.
 *
 * One submission, two documents: a Code Analysis Report (what the codebase is
 * and how it hangs together) and an Integration Reference Manual (how to run,
 * configure and support it). Both are written from the same analysed source, so
 * the page takes the source once — a .zip or a repository to clone — and then
 * shows the pair side by side with their own exports rather than dropping the
 * consultant back into the documentation list to hunt for them.
 *
 * Progress is polled off the shared job-status endpoint (~3s); the finished job
 * carries the documentation ids, which are loaded and rendered in place.
 */

// The two documents, in the order they are presented. The blurbs are what the
// consultant needs to pick the right one to send.
const CODEDOC_KINDS = [
  {
    kind: 'code_analysis_report',
    label: 'Code Analysis Report',
    icon: FileSearch,
    blurb: 'What the codebase does, how it is structured, and where the risk sits.',
  },
  {
    kind: 'integration_reference_manual',
    label: 'Integration Reference Manual',
    icon: BookMarked,
    blurb: 'How to configure, run, test and support the integration day to day.',
  },
];

// Provider metadata for the repository source. Mirrors the repo section of the
// multi-source documentation upload so the two flows behave identically.
const REPO_PROVIDERS = {
  'github': {
    label: 'GitHub',
    urlPlaceholder: 'https://github.com/{owner}/{repo}',
    tokenLabel: 'Access Token (optional)',
    tokenPlaceholder: 'GitHub token for private repositories',
    needsUsername: false,
  },
  'azure-devops': {
    label: 'Azure DevOps',
    urlPlaceholder: 'https://dev.azure.com/{org}/{project}/_git/{repo}',
    tokenLabel: 'Personal Access Token (PAT)',
    tokenPlaceholder: 'Your Azure DevOps PAT with Code (Read) scope',
    needsUsername: false,
  },
  'bitbucket': {
    label: 'Bitbucket',
    urlPlaceholder: 'https://bitbucket.org/{workspace}/{repo}',
    tokenLabel: 'App Password',
    tokenPlaceholder: 'Bitbucket App Password',
    needsUsername: true,
  },
  'gitlab': {
    label: 'GitLab',
    urlPlaceholder: 'https://gitlab.com/{owner}/{repo}',
    tokenLabel: 'Personal Access Token (optional)',
    tokenPlaceholder: 'GitLab personal access token',
    needsUsername: false,
  },
};

// ---------------------------------------------------------------------------
// Markdown components — the same Notion-inspired typography as the other
// document views, minus the per-section feedback affordance (these documents
// are read here and reviewed in their project).
// ---------------------------------------------------------------------------
const markdownComponents = {
  h1: ({ children }) => (
    <h1 className="text-[26px] font-bold text-slate-900 mt-10 mb-4 pb-3 border-b border-slate-200/80 font-['Plus_Jakarta_Sans'] first:mt-0">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-[21px] font-semibold text-slate-800 mt-9 mb-3 pb-2 border-b border-slate-100 font-['Plus_Jakarta_Sans']">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-lg font-semibold text-slate-800 mt-7 mb-2.5 font-['Plus_Jakarta_Sans']">{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className="text-base font-semibold text-slate-700 mt-6 mb-2 font-['Plus_Jakarta_Sans']">{children}</h4>
  ),
  p: ({ children }) => (
    <p className="text-[15px] leading-[1.8] text-slate-600 my-4">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="my-4 ml-1 space-y-2 list-none">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="my-4 ml-1 space-y-2 list-decimal list-inside marker:text-slate-400 marker:font-medium">{children}</ol>
  ),
  li: ({ children }) => (
    <li className="text-[15px] leading-[1.8] text-slate-600 pl-1 relative before:content-[''] before:absolute before:left-[-12px] before:top-[11px] before:w-[5px] before:h-[5px] before:rounded-full before:bg-slate-300">{children}</li>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-[3px] border-[#2B8FD8]/40 bg-sky-50/40 pl-5 pr-4 py-3 my-6 rounded-r text-slate-600">
      {children}
    </blockquote>
  ),
  table: ({ children }) => (
    <div className="overflow-x-auto my-8 rounded-lg border border-slate-200">
      <table className="min-w-full text-sm">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-slate-50/80 border-b border-slate-200">{children}</thead>,
  th: ({ children }) => (
    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">{children}</th>
  ),
  td: ({ children }) => (
    <td className="px-4 py-3 text-sm text-slate-600 border-t border-slate-100">{children}</td>
  ),
  tr: ({ children }) => (
    <tr className="hover:bg-slate-50/50 transition-colors">{children}</tr>
  ),
  hr: () => <hr className="my-10 border-slate-200" />,
  a: ({ href, children }) => (
    <a href={href} className="text-[#2B8FD8] hover:text-[#2075b3] underline underline-offset-2 decoration-[#2B8FD8]/30 hover:decoration-[#2B8FD8] transition-colors" target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  strong: ({ children }) => <strong className="font-semibold text-slate-800">{children}</strong>,
  em: ({ children }) => <em className="text-slate-600">{children}</em>,
  code: ({ inline, className, children }) => {
    if (inline) {
      return (
        <code className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded text-[13px] font-mono border border-slate-200/60">
          {children}
        </code>
      );
    }
    const lang = className?.replace('language-', '') || '';
    return (
      <div className="my-6 rounded-lg overflow-hidden border border-slate-200/60 shadow-sm">
        {lang && (
          <div className="bg-slate-800 px-4 py-1.5 text-[10px] font-mono text-slate-400 uppercase tracking-wider border-b border-slate-700/30">
            {lang}
          </div>
        )}
        <pre className="bg-[#1e1e2e] text-slate-200 p-5 overflow-x-auto">
          <code className="text-[13px] leading-6 font-mono">{children}</code>
        </pre>
      </div>
    );
  },
};

// The completed job may name the pair as a list or, if only one landed, as the
// single documentation_id — read through both rather than assuming.
const collectDocIds = (job) => {
  const raw = job?.documentation_ids
    || job?.result?.documentation_ids
    || (job?.documentation_id ? [job.documentation_id] : []);
  const list = Array.isArray(raw) ? raw : [raw];
  return Array.from(new Set(list.filter(Boolean)));
};

// Order the loaded documents the way CODEDOC_KINDS lists them; anything with an
// unexpected doc_kind still gets shown, just last.
const orderDocs = (docs) => {
  const rank = (d) => {
    const i = CODEDOC_KINDS.findIndex(k => k.kind === d?.doc_kind);
    return i === -1 ? CODEDOC_KINDS.length : i;
  };
  return [...docs].sort((a, b) => rank(a) - rank(b));
};

const kindMeta = (doc, index) =>
  CODEDOC_KINDS.find(k => k.kind === doc?.doc_kind) || CODEDOC_KINDS[index] || CODEDOC_KINDS[0];

const CodeDocPage = () => {
  const { canUpload, user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedProject = searchParams.get('project');

  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedProject, setSelectedProject] = useState(preselectedProject || '');
  const [title, setTitle] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);

  // Optional repository source (instead of, or alongside, the zips)
  const [showRepo, setShowRepo] = useState(false);
  const [repoProvider, setRepoProvider] = useState('github');
  const [repoUrl, setRepoUrl] = useState('');
  const [repoBranch, setRepoBranch] = useState('');
  const [repoToken, setRepoToken] = useState('');
  const [repoUsername, setRepoUsername] = useState('');

  // Job tracking
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [jobElapsedMin, setJobElapsedMin] = useState(0);
  const pollInterval = useRef(null);
  const elapsedTimer = useRef(null);

  // Generated documents
  const [docs, setDocs] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [customersRes, projectsRes] = await Promise.all([
        getCustomers(),
        getProjects(),
      ]);
      setCustomers(customersRes.data);
      setProjects(projectsRes.data);

      if (preselectedProject) {
        const proj = projectsRes.data.find(p => p.id === preselectedProject);
        if (proj) {
          setSelectedCustomer(proj.customer_id);
          setSelectedProject(proj.id);
        }
      }
    } catch (error) {
      toast.error('Failed to load customers and projects');
    } finally {
      setLoading(false);
    }
  }, [preselectedProject]);

  useEffect(() => {
    if (!canUpload) {
      navigate('/documentation');
      return;
    }
    fetchData();
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
      if (elapsedTimer.current) clearInterval(elapsedTimer.current);
    };
  }, [canUpload, navigate, fetchData]);

  const filteredProjects = selectedCustomer
    ? projects.filter(p => p.customer_id === selectedCustomer)
    : projects;

  // ---- Load the pair the finished job produced ----
  const loadGeneratedDocs = useCallback(async (job) => {
    const ids = collectDocIds(job);
    if (ids.length === 0) {
      toast.error('The job finished but returned no documents');
      return;
    }
    setLoadingDocs(true);
    try {
      const results = await Promise.all(ids.map(docId => getDocumentationById(docId)));
      setDocs(orderDocs(results.map(r => r.data).filter(Boolean)));
    } catch (error) {
      toast.error('Generated the documents but failed to load them — open the project to view them');
    } finally {
      setLoadingDocs(false);
    }
  }, []);

  const startPolling = useCallback((jId) => {
    if (pollInterval.current) clearInterval(pollInterval.current);
    if (elapsedTimer.current) clearInterval(elapsedTimer.current);

    const startTime = Date.now();
    setJobElapsedMin(0);

    elapsedTimer.current = setInterval(() => {
      setJobElapsedMin(Math.floor((Date.now() - startTime) / 60000));
    }, 30000);

    pollInterval.current = setInterval(async () => {
      // Poll deadline: a backend restart mid-job can strand it forever. Two
      // documents off one codebase take a while, so allow ~20 min before
      // surfacing a retry prompt instead of an infinite spinner.
      if (Date.now() - startTime > 20 * 60 * 1000) {
        clearInterval(pollInterval.current);
        clearInterval(elapsedTimer.current);
        pollInterval.current = null;
        elapsedTimer.current = null;
        setJobStatus((s) => (s ? { ...s, status: 'failed', error: 'This page has stopped waiting after 20 minutes. The job may still finish — look for the documents under Documentation before starting again.' } : s));
        toast.error('This page has stopped waiting after 20 minutes — the job may still finish.');
        return;
      }
      try {
        const res = await getJobStatus(jId);
        setJobStatus(res.data);

        if (res.data.status === 'complete') {
          clearInterval(pollInterval.current);
          clearInterval(elapsedTimer.current);
          pollInterval.current = null;
          elapsedTimer.current = null;
          toast.success('Code documentation generated!');
          loadGeneratedDocs(res.data);
        } else if (res.data.status === 'failed') {
          clearInterval(pollInterval.current);
          clearInterval(elapsedTimer.current);
          pollInterval.current = null;
          elapsedTimer.current = null;
          toast.error(res.data.error || 'Code documentation failed');
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    }, 3000);
  }, [loadGeneratedDocs]); // eslint-disable-line react-hooks/exhaustive-deps -- getJobStatus is a stable module import

  const handleSubmit = async () => {
    const linkedRepoUrl = repoUrl.trim();
    const hasRepo = !!linkedRepoUrl;

    if (!title.trim()) {
      toast.error('Please enter a title for the documentation');
      return;
    }
    if (files.length === 0 && !hasRepo) {
      toast.error('Please upload a code zip or connect a repository');
      return;
    }

    // The server takes only zips here, so any loose code files go up as one zip
    // named after the title (it becomes their folder name in the documents).
    const isZip = (f) => /\.zip$/i.test(f.name);
    const uploads = files.filter(isZip);
    const loose = files.filter((f) => !isZip(f));
    if (loose.length > 0) {
      const zipName = title.trim().replace(/[^\w.-]+/g, '-').replace(/^-+|-+$/g, '') || 'source-files';
      try {
        uploads.push(await zipLooseFiles(loose, `${zipName}.zip`));
      } catch {
        toast.error('One of the selected files could not be read — remove it and add it again');
        return;
      }
    }

    const formData = new FormData();
    formData.append('title', title);
    uploads.forEach((f) => formData.append('files', f));
    if (selectedProject) formData.append('project_id', selectedProject);
    if (hasRepo) {
      formData.append('repo_url', linkedRepoUrl);
      formData.append('provider', repoProvider);
      if (repoBranch.trim()) formData.append('branch', repoBranch.trim());
      if (repoToken) formData.append('repo_token', repoToken);
      if (REPO_PROVIDERS[repoProvider].needsUsername && repoUsername.trim()) {
        formData.append('repo_username', repoUsername.trim());
      }
    }

    try {
      setDocs([]);
      setJobStatus({ status: 'uploading', progress: 0, message: 'Uploading the codebase...' });
      // Percent, MB sent and time left go onto the job card while it is still uploading
      const response = await createCodeDocs(formData, (evt) => {
        setJobStatus((s) => (s?.status === 'uploading' ? uploadingStatus(evt) : s));
      });
      const jId = response.data.job_id;
      setJobId(jId);
      setJobStatus({ status: 'processing', progress: 5, message: 'Analysing the codebase, then writing both documents...' });
      startPolling(jId);
    } catch (error) {
      setJobStatus(null);
      setJobId(null);
      toast.error(error.response?.data?.detail || 'Failed to start code documentation');
    }
  };

  const resetJob = () => {
    if (pollInterval.current) clearInterval(pollInterval.current);
    if (elapsedTimer.current) clearInterval(elapsedTimer.current);
    setJobId(null);
    setJobStatus(null);
    setFiles([]);
    setTitle('');
    setDocs([]);
    setRepoUrl('');
    setRepoBranch('');
    setRepoToken('');
    setRepoUsername('');
  };

  // ---- Authenticated downloads (shared helper; a failure toasts the server's reason) ----
  const downloadDoc = (doc, format) => {
    if (!doc || !token) return;
    const url = format === 'pdf' ? exportDocumentationPdf(doc.id) : exportDocumentationWord(doc.id);
    const label = format === 'pdf' ? 'PDF' : 'Word';
    toast.promise(downloadExport(url, token, `${doc.title}.${format === 'pdf' ? 'pdf' : 'docx'}`), {
      loading: `Preparing ${label}…`,
      success: `Exported as ${label}!`,
      error: (err) => err.message,
    });
  };

  const isProcessing = jobStatus && !['complete', 'failed'].includes(jobStatus.status);
  const hasResults = docs.length > 0;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-[#2B8FD8]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={`p-8 mx-auto ${hasResults ? 'max-w-5xl' : 'max-w-3xl'}`}>
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="codedoc-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']" data-testid="codedoc-page-title">
              Code Documentation
            </h1>
            <p className="text-slate-500 text-sm">
              One codebase in, two documents out — a Code Analysis Report and an Integration Reference Manual
            </p>
          </div>
        </div>

        {/* Job Status Card */}
        <JobProgressTracker
          jobStatus={jobStatus}
          jobElapsedMin={jobElapsedMin}
          isProcessing={isProcessing}
          onViewDocs={() => {
            const el = document.getElementById('codedoc-results');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          onReset={resetJob}
          normalTime="8 to 12 minutes"
        />

        {/* Main form - hidden while processing or once the documents exist */}
        {!isProcessing && jobStatus?.status !== 'complete' && (
          <div className="space-y-6">
            {/* What will be produced */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {CODEDOC_KINDS.map(({ kind, label, icon: Icon, blurb }) => (
                <div key={kind} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`codedoc-preview-${kind}`}>
                  <span className="flex items-center gap-2 mb-1.5">
                    <Icon className="h-4 w-4 text-[#2B8FD8]" />
                    <span className="text-sm font-semibold text-slate-700">{label}</span>
                  </span>
                  <span className="block text-xs leading-relaxed text-slate-500">{blurb}</span>
                </div>
              ))}
            </div>

            {/* Core inputs */}
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-base font-['Plus_Jakarta_Sans']">What are we documenting?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Title *</Label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., Cygnett AusPost EDI Integration"
                    data-testid="codedoc-title-input"
                  />
                  <p className="text-xs text-slate-500">
                    Both documents are named from this, so use the integration's real name.
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Author</Label>
                  <div
                    className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600"
                    data-testid="codedoc-author-readonly"
                  >
                    <UserRound className="h-4 w-4 text-slate-400" />
                    <span className="font-medium text-slate-700">{user?.name || 'You'}</span>
                    <span className="text-xs text-slate-400">— both documents are prepared in your name</span>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Customer <span className="text-slate-400 font-normal">(optional)</span></Label>
                    <Select
                      value={selectedCustomer}
                      onValueChange={(v) => { setSelectedCustomer(v); setSelectedProject(''); }}
                    >
                      <SelectTrigger data-testid="codedoc-customer-select">
                        <SelectValue placeholder="Select customer" />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Project <span className="text-slate-400 font-normal">(optional)</span></Label>
                    <Select value={selectedProject} onValueChange={setSelectedProject}>
                      <SelectTrigger data-testid="codedoc-project-select">
                        <SelectValue placeholder="Select project" />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredProjects.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Source — a zip, a repository, or both */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                <FileSearch className="h-4 w-4" />
                The codebase *
              </h3>
              <FileUploadTab
                multiple
                file={files}
                setFile={setFiles}
                onUpload={handleSubmit}
                disabled={!title.trim() || (files.length === 0 && !repoUrl.trim())}
                accept=".zip,.cs,.py,.sql,.xml,.xsl,.xslt,.xsd,.json,.js,.ts,.config,.csproj,.sln,.txt,.md,.yaml,.yml"
                hint="Code ZIP or B1iF package export (nested zips supported), C#, Python, SQL, XML / XSL, JSON, Config files — max 100MB each"
              >
                {/* Repository source, cloned and analysed with anything uploaded above */}
                <div className="border border-slate-200 rounded-lg">
                  <button
                    type="button"
                    className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-slate-50 rounded-lg transition-colors"
                    onClick={() => setShowRepo(!showRepo)}
                    data-testid="codedoc-repo-toggle"
                  >
                    <span className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <GitBranch className="h-4 w-4" />
                      Connect a code repository instead <span className="text-slate-400 font-normal">(optional)</span>
                    </span>
                    <span className="flex items-center gap-2">
                      {repoUrl.trim() && (
                        <span className="text-xs font-medium text-[#2B8FD8] bg-blue-50 rounded-full px-2 py-0.5">
                          {REPO_PROVIDERS[repoProvider].label} linked
                        </span>
                      )}
                      {showRepo
                        ? <ChevronUp className="h-4 w-4 text-slate-400" />
                        : <ChevronDown className="h-4 w-4 text-slate-400" />}
                    </span>
                  </button>

                  {showRepo && (
                    <div className="px-4 pb-4 pt-4 space-y-4 border-t border-slate-200">
                      <p className="text-xs text-slate-500">
                        The repository is cloned and analysed together with anything uploaded above —
                        both documents are written from the same source.
                      </p>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Provider</Label>
                          <Select value={repoProvider} onValueChange={setRepoProvider}>
                            <SelectTrigger data-testid="codedoc-repo-provider-select">
                              <SelectValue placeholder="Select provider" />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(REPO_PROVIDERS).map(([value, cfg]) => (
                                <SelectItem key={value} value={value}>{cfg.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <GitBranch className="h-4 w-4" />
                            Branch <span className="text-slate-400 font-normal">(optional)</span>
                          </Label>
                          <Input
                            value={repoBranch}
                            onChange={(e) => setRepoBranch(e.target.value)}
                            placeholder="e.g. main, develop, feature/my-branch"
                            data-testid="codedoc-repo-branch-input"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Link className="h-4 w-4" />
                          Repository URL
                        </Label>
                        <Input
                          value={repoUrl}
                          onChange={(e) => setRepoUrl(e.target.value)}
                          placeholder={REPO_PROVIDERS[repoProvider].urlPlaceholder}
                          data-testid="codedoc-repo-url-input"
                        />
                        <p className="text-xs text-slate-500">
                          Leave blank to document the uploaded files only
                        </p>
                      </div>

                      <div className={REPO_PROVIDERS[repoProvider].needsUsername ? 'grid grid-cols-2 gap-4' : 'space-y-2'}>
                        {REPO_PROVIDERS[repoProvider].needsUsername && (
                          <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                              <KeyRound className="h-4 w-4" />
                              Username
                            </Label>
                            <Input
                              value={repoUsername}
                              onChange={(e) => setRepoUsername(e.target.value)}
                              placeholder="Your Bitbucket username"
                              data-testid="codedoc-repo-username-input"
                            />
                          </div>
                        )}
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <KeyRound className="h-4 w-4" />
                            {REPO_PROVIDERS[repoProvider].tokenLabel}
                          </Label>
                          <Input
                            type="password"
                            value={repoToken}
                            onChange={(e) => setRepoToken(e.target.value)}
                            placeholder={REPO_PROVIDERS[repoProvider].tokenPlaceholder}
                            data-testid="codedoc-repo-token-input"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-slate-500">
                        Required for private repositories only
                      </p>
                    </div>
                  )}
                </div>
              </FileUploadTab>
            </div>
          </div>
        )}

        {/* ---- Results: both generated documents ---- */}
        {loadingDocs && (
          <Card className="border-slate-200">
            <CardContent className="p-8 flex flex-col items-center text-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-[#2B8FD8]" />
              <p className="text-sm font-medium text-slate-700">Loading the generated documents...</p>
            </CardContent>
          </Card>
        )}

        {hasResults && (
          <div id="codedoc-results" className="space-y-4" data-testid="codedoc-results">
            <Tabs defaultValue="doc-0" className="space-y-4">
              <TabsList
                className="grid w-full h-12"
                style={{ gridTemplateColumns: `repeat(${docs.length}, minmax(0, 1fr))` }}
              >
                {docs.map((doc, i) => {
                  const meta = kindMeta(doc, i);
                  const Icon = meta.icon;
                  return (
                    <TabsTrigger
                      key={doc.id}
                      value={`doc-${i}`}
                      className="flex items-center gap-2"
                      data-testid={`codedoc-tab-${i}`}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="truncate">{meta.label}</span>
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {docs.map((doc, i) => {
                const meta = kindMeta(doc, i);
                return (
                  <TabsContent key={doc.id} value={`doc-${i}`}>
                    <Card className="border-slate-200">
                      <CardHeader className="border-b border-slate-100">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div className="min-w-0">
                            <CardTitle className="text-base font-['Plus_Jakarta_Sans'] truncate" data-testid={`codedoc-doc-title-${i}`}>
                              {doc.title}
                            </CardTitle>
                            <p className="text-xs text-slate-500 mt-1">{meta.blurb}</p>
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                              <Badge variant="outline" className="text-[10px]">{meta.label}</Badge>
                              {doc.version != null && <Badge variant="outline" className="text-[10px]">v{doc.version}</Badge>}
                              {doc.created_by_name && (
                                <span className="text-[11px] text-slate-400 flex items-center gap-1">
                                  <UserRound className="h-3 w-3" />{doc.created_by_name}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => downloadDoc(doc, 'word')}
                              className="h-8 text-xs"
                              data-testid={`codedoc-export-word-${i}`}
                            >
                              <Download className="h-3.5 w-3.5 mr-1.5" /> Word
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => downloadDoc(doc, 'pdf')}
                              className="h-8 text-xs"
                              data-testid={`codedoc-export-pdf-${i}`}
                            >
                              <FileDown className="h-3.5 w-3.5 mr-1.5" /> PDF
                            </Button>
                            {doc.project_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/projects/${doc.project_id}`)}
                                className="h-8 text-xs text-slate-500"
                                data-testid={`codedoc-open-project-${i}`}
                              >
                                <FolderOpen className="h-3.5 w-3.5 mr-1.5" /> Open in project
                              </Button>
                            )}
                            {doc.project_id && (
                              // Next step of the change-request flow: code documented -> write the CR.
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigate(`/design-builder?project=${doc.project_id}`)}
                                className="h-8 text-xs text-[#2B8FD8]"
                                data-testid={`codedoc-create-cr-${i}`}
                              >
                                <PencilRuler className="h-3.5 w-3.5 mr-1.5" /> Create change request
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="p-0">
                        <article className="px-4 py-6 md:px-10 md:py-8">
                          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                            {doc.content || ''}
                          </ReactMarkdown>
                        </article>
                      </CardContent>
                    </Card>
                  </TabsContent>
                );
              })}
            </Tabs>

            <Button variant="outline" onClick={resetJob} className="w-full h-11" data-testid="codedoc-generate-another-btn">
              Document another codebase
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default CodeDocPage;
